// Service to interact with the Todos API
// Use the full URL instead of a relative path to ensure it works across all pages
const API_URL = window.location.origin + '/api/todos';
console.log('Todo API URL:', API_URL); // Log the API URL for debugging

// Get or set the sync ID in localStorage
const getSyncId = () => {
  return localStorage.getItem('sync_id');
};

const setSyncId = (syncId) => {
  localStorage.setItem('sync_id', syncId);
  console.log('Saved sync ID to localStorage:', syncId);
};

export const todoService = {
  // Get todos from the API
  async getTodos() {
    try {
      console.log('Fetching todos from API...');
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add sync ID if available
      const syncId = getSyncId();
      if (syncId) {
        console.log('Using sync ID:', syncId);
        headers['X-Sync-ID'] = syncId;
      }
      
      const response = await fetch(API_URL, {
        method: 'GET',
        credentials: 'include', // Important: Include cookies in the request
        headers: headers
      });
      
      console.log('Fetch response status:', response.status);
      
      // Check if we got a sync ID in the response
      const responseSyncId = response.headers.get('X-Sync-ID');
      if (responseSyncId) {
        console.log('Received sync ID from server:', responseSyncId);
        setSyncId(responseSyncId);
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching todos:', errorText);
        
        // If we can't connect to the API, try to get todos from localStorage
        const localTodos = localStorage.getItem('todos');
        const localPriorityDividerIndex = localStorage.getItem('priorityDividerIndex');
        
        if (localTodos) {
          console.log('Using todos from localStorage');
          return {
            todos: JSON.parse(localTodos),
            priorityDividerIndex: localPriorityDividerIndex ? parseInt(localPriorityDividerIndex, 10) : 0
          };
        }
        
        throw new Error(`Failed to fetch todos: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Fetched todos:', data);
      
      // Store todos in localStorage as a backup
      localStorage.setItem('todos', JSON.stringify(data.todos || []));
      
      // Store priority divider index in localStorage
      if (data.priorityDividerIndex !== undefined) {
        localStorage.setItem('priorityDividerIndex', data.priorityDividerIndex.toString());
      }
      
      return {
        todos: data.todos || [],
        priorityDividerIndex: data.priorityDividerIndex !== undefined ? data.priorityDividerIndex : 0
      };
    } catch (error) {
      console.error('Error in getTodos:', error);
      
      // If there's an error, try to get todos from localStorage
      const localTodos = localStorage.getItem('todos');
      const localPriorityDividerIndex = localStorage.getItem('priorityDividerIndex');
      
      if (localTodos) {
        console.log('Using todos from localStorage due to error');
        return {
          todos: JSON.parse(localTodos),
          priorityDividerIndex: localPriorityDividerIndex ? parseInt(localPriorityDividerIndex, 10) : 0
        };
      }
      
      // If no local todos, return empty array
      return {
        todos: [],
        priorityDividerIndex: 0
      };
    }
  },

  // Save todos to the API
  async saveTodos(todos, priorityDividerIndex = 0) {
    try {
      console.log('Saving todos to API...');
      
      // Store todos in localStorage as a backup
      localStorage.setItem('todos', JSON.stringify(todos));
      localStorage.setItem('priorityDividerIndex', priorityDividerIndex.toString());
      
      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add sync ID if available
      const syncId = getSyncId();
      if (syncId) {
        console.log('Using sync ID for save:', syncId);
        headers['X-Sync-ID'] = syncId;
      }
      
      const response = await fetch(API_URL, {
        method: 'POST',
        credentials: 'include', // Important: Include cookies in the request
        headers: headers,
        body: JSON.stringify({
          todos: todos,
          priorityDividerIndex: priorityDividerIndex
        })
      });
      
      console.log('Save response status:', response.status);
      
      // Check if we got a sync ID in the response
      const responseSyncId = response.headers.get('X-Sync-ID');
      if (responseSyncId) {
        console.log('Received sync ID from server after save:', responseSyncId);
        setSyncId(responseSyncId);
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error saving todos:', errorText);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in saveTodos:', error);
      return false;
    }
  },
  
  // Set a custom sync ID for cross-device syncing
  setSyncId(syncId) {
    if (!syncId || typeof syncId !== 'string' || syncId.trim() === '') {
      throw new Error('Invalid sync ID');
    }
    
    setSyncId(syncId.trim());
    return true;
  },
  
  // Get the current sync ID
  getSyncId() {
    return getSyncId();
  },
  
  // Generate a new sync ID
  generateSyncId() {
    // Generate a random ID that's easy to type and share
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed similar looking characters
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Add dashes for readability
    const formattedId = `${id.substring(0, 4)}-${id.substring(4, 8)}`;
    setSyncId(formattedId);
    return formattedId;
  }
}; 